import { Box, Button, Stack, Typography } from '@mui/material'
import { Divider } from '@mui/material'
import { Icons } from 'utoniq-ui'

import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ArtistThemeProvider, styles } from '@/components/styles'
import { config } from '@/config'
import { StorageImage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  artistId: string
  loading: boolean
  app?: string
}

export type FeatureCellProps = {
  app: State['app']
  loading: State['loading']
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const ConsoleMembershipExplainTemplate: Template<State, Action> = ({
  state,
}) => {
  const { routerNames, routerPages } = useRouter()
  const { t } = useTranslation('consoleMembershipExplain')

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleMembershipExplain} />

      <ConsoleBaseLayout
        header={{
          back: true,
          defaultBackPage: routerPages.ConsoleDashboard,
          title: t('title'),
        }}
      >
        <Stack sx={{ mx: 5, mt: 7 }}>
          <Typography variant="h2">{t('monthlyMembership')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('content')}
          </Typography>
          <Box sx={{ mt: 6, mb: 2 }}>
            <Typography variant="h4">{t('membership')}</Typography>
          </Box>
          <Divider />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 2, mb: 4 }}
          >
            {t('functions')}
          </Typography>
        </Stack>
        <FeatureCell app={state.app} loading={state.loading} />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

const FeatureCell: Cell<FeatureCellProps> = ({ app, loading }) => {
  const { t } = useTranslation('consoleMembershipExplain')
  const { routerPages } = useRouter()

  const features = [
    {
      title: t('postLimit'),
      content: t('postLimitDetail'),
    },
    {
      title: t('saleLimit'),
      content: t('saleLimitDetail'),
    },
    {
      title: t('communityPost'),
      content: t('communityPostDetail'),
    },
    {
      title: t('membershipManagement'),
      content: t('membershipManagementDetail'),
    },
  ]

  return (
    <CellFrame sx={{ pb: styles.safeAreaBottom }}>
      <Stack spacing={4} sx={{ pb: 1, px: 5, pt: 2 }}>
        {features.map((feature, index) => (
          <Stack key={index} direction="row" alignItems="center" spacing={4}>
            <StorageImage
              style={{ borderRadius: '50%' }}
              target="common"
              src={`images/console/membershipExplain_${index + 1}.png`}
              width={120}
              height={120}
            />
            <Stack flex={1} spacing={1}>
              <Typography variant="h4">{feature.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {feature.content}
              </Typography>
            </Stack>
          </Stack>
        ))}
      </Stack>
      {app && (
        <Stack
          sx={{
            mt: 5,
            mb: 4,
            mx: 5,
            borderRadius: 2,
            border: 1,
            borderColor: 'grey.100',
            pt: 5,
            px: 7,
            pb: '64px',
          }}
          alignItems="center"
        >
          <Typography variant="body2">{t('cannotOpen')}</Typography>
          <Typography variant="subtitle1" color="text.hint" sx={{ mt: 4 }}>
            {t('searchBrowser')}
          </Typography>
          <Button
            endIcon={
              <Box>
                <Icons.Search fontSize="medium" />
              </Box>
            }
            sx={{ width: 275, mt: 2 }}
            disabled
          >
            <Typography variant="subtitle1" width="100%" sx={{ ml: 7 }}>
              {app}
            </Typography>
          </Button>
        </Stack>
      )}
      {!app && (
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{ my: 8, pb: '56px' }}
        >
          <Button
            size="large"
            color="primary"
            sx={{ width: 244 }}
            disabled={!config.modules.membership}
            href={routerPages.ConsoleMembershipCreate}
          >
            {t('openMembership')}
          </Button>
        </Stack>
      )}
    </CellFrame>
  )
}
